import React, { useState, useEffect } from 'react';
import { Autocomplete, Box, Button, TextField, Typography, useMediaQuery, useTheme } from '@mui/material';
import axios from 'axios';
import reqProvider from '../../util/requests/reqProvider';
import { useNotify } from '../../util/hooks/notify/Notification';
import RegisteredCompanies from './RegisteredCompanies';
import { useSelector } from 'react-redux';
import { RootState } from '../../globalStore/store';

interface Company {
  name: string;
  address: string;
  id: number;
  collection: string;
  full_tax_number: string;
  short_name: string;
}

const CompanyRegistration: React.FC = () => {
  const [searchParam, setSearchParam] = useState<string>('');
  const [companyList, setCompanyList] = useState<Company[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<Company | null | undefined>(null);
  const [confirmCompany, setConfirmCompany] = useState<boolean>(false);
  const [contactName, setContactName] = useState<string>('');
  const [contactPhone, setContactPhone] = useState<string>('+36');
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [registered, setRegistered] = useState<boolean>(false);
  const notify = useNotify();
  const userName: string = useSelector((state: RootState) => state.user.userName);
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));


  useEffect(() => {
    if (searchParam.length > 2) {
      axios.get(`https://dev.api.cegjelzo.com/api/v2/autocomplete?search=${searchParam}`, {
        headers: {
          'X-Api-Key': process.env.REACT_APP_COMPANY_INFO_API_KEY,
          'X-Client-Id': process.env.REACT_APP_COMPANY_INFO_CLIENT_ID,
          'Content-Type': 'application/json',
        }
      })
        .then(res => {
          setCompanyList(res.data.result);
        })
        .catch(err => {
          console.error(err);
          setCompanyList([]);
        });
    } else {
      setCompanyList([]);
    }
  }, [searchParam]);

  const handleRegistration = async () => {
    const newErrors: { [key: string]: string } = {};
    if (!contactName) newErrors.contactName = 'A mező kitöltése kötelező';
    if (!contactPhone) newErrors.contactPhone = 'Telefonszám megadása kötelező pl.: +36301234567';
    if (!validatePhoneNumber(contactPhone)) newErrors.contactPhone = 'Telefonszám megadása kötelező pl.: +36301234567';

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    const companyData = {
      name: selectedCompany?.name,
      country: 'Magyarország',
      taxNumber: selectedCompany?.full_tax_number,
      prefix: contactPhone.substring(0, 3),
      contactPhone: contactPhone.substring(3),
      contactName,
      fullAddress: selectedCompany?.address,
      shortName: selectedCompany?.short_name ? selectedCompany?.short_name : null,
      contactEmail: userName ? userName : ''
    };

    try {
      const response = await reqProvider.post('company', companyData);

      if (response.status === 200) {
        notify('Sikeres regisztráció', 'success');
        setRegistered(true);
        setSearchParam('');
        setCompanyList([]);
        setSelectedCompany(null);
        setConfirmCompany(false);
        setContactName('');
        setContactPhone('+36');
        setErrors({});
      } else {
        notify('Sikertelen regisztráció', 'error');
      }
    } catch (err) {
      notify('Sikertelen regisztráció', 'error');
    }
  };

  const validatePhoneNumber = (phone: string) => {
    const phonePattern = /^\+\d{11}$/;
    return phonePattern.test(phone);
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (/^\+\d*$/.test(value)) {
      setContactPhone(value);
      setErrors((prevErrors) => ({ ...prevErrors, contactPhone: '' }));
    } else {
      setErrors((prevErrors) => ({ ...prevErrors, contactPhone: 'Telefonszám megadása kötelező pl.: +36301234567' }));
    }
  };

  return (
  registered ? (
    <RegisteredCompanies setRegistered={setRegistered}/>
    )
   : (
    <Box sx={{display: 'flex', flexDirection: 'column', minHeight: '260px', justifyContent: 'space-around', alignItems: 'center', marginTop: '20px', width: isSmallScreen ? '100%' : '350px'}}>
      <Typography>{confirmCompany ? "Ellenőrizd az adatokat!" : "Melyik céget képviseled?"}</Typography>
      <Box sx={{width: '100%'}}>
        <Typography variant="caption">{"Mi a cég neve?"}</Typography>
        <Autocomplete
          sx={{marginTop: '5px'}}
          fullWidth
          value={selectedCompany}
          options={companyList}
          getOptionLabel={option => option.name + ' - ' +option.full_tax_number || ""}
          onChange={(event, value) => setSelectedCompany(value || null)}
          onInputChange={(event, value) => setSearchParam(value)}
          renderInput={(params) => <TextField {...params} label="Cég neve" />}
          disableClearable={false}
        />
        {confirmCompany && <Button onClick={()=>{setSelectedCompany(null)}}>Módosítás</Button>}
      </Box>
      {!confirmCompany && <Button disabled={!selectedCompany} onClick={()=>{setConfirmCompany(true)}} variant={'contained'}>Tovább a cégadatokhoz</Button>}
      {confirmCompany &&
        <>
          <Typography fontWeight={500}>{"Cégadatok"}</Typography>
          <Box sx={{marginTop: '10px', marginBottom: '15px'}}>
          <Box>
            <Typography variant="caption">{"Cég neve:"}</Typography>
            <Typography>{selectedCompany?.short_name ? selectedCompany?.short_name : selectedCompany?.name}</Typography>
          </Box>
            <hr style={{width: '100%'}}/>
          <Box>
            <Typography variant="caption">{"Adószáma:"}</Typography>
            <Typography>{selectedCompany?.full_tax_number}</Typography>
          </Box>
            <hr style={{width: '100%'}}/>
          <Box>
            <Typography variant="caption">{"Székhelye:"}</Typography>
            <Typography>{selectedCompany?.address}</Typography>
          </Box>
            <hr style={{width: '100%'}}/>
          <Box>
            <TextField
              type={'text'}
              variant={'standard'}
              required={true}
              label={"Kapcsolattartó neve"}
              fullWidth
              value={contactName}
              onChange={(e) => setContactName(e.target.value)}
              error={!!errors.contactName}
              helperText={errors.contactName}
            />
            <TextField
              sx={{marginTop: '10px'}}
              type={'tel'}
              variant={'standard'}
              required={true}
              label={"Telefonszáma"}
              fullWidth
              value={contactPhone}
              onChange={handlePhoneChange}
              error={!!errors.contactPhone}
              helperText={errors.contactPhone}
            />
          </Box>
          </Box>
          <Button disabled={!selectedCompany || !selectedCompany.name.length} onClick={()=>{handleRegistration()}} variant={'contained'}>Cég regisztrálása</Button>
        </>
      }
    </Box>
  )
  );
}

export default CompanyRegistration;